import { RefObject } from 'react'

import { userManager } from '../oidc-config'

import { LOCALHOST_HOST } from './constants'

const headers = {
  credentials: 'include',
  'Content-Type': 'application/json',
}

// Dev proxy handling for dynamic hostnames
const getPath = (url: string) => {
  const isLocalhost = document.location.hostname === 'localhost' && !globalThis.isJest

  let parsedUrl
  try {
    parsedUrl = new URL(url)
  } catch {
    // Do nothing, this url doesn't have a hostname
  }

  // On localhost, proxy all requests that don't match the regExp and have a hostname
  if (isLocalhost) return parsedUrl ? `https://${parsedUrl.host}${parsedUrl.pathname}${parsedUrl.search}` : `${LOCALHOST_HOST}/${url}`
  // In jest tests, remove the hostname and return the path
  if (globalThis.isJest && parsedUrl) return `${parsedUrl.pathname}${parsedUrl.search}`

  return url
}

const get = async (url: string, args?: RequestInit | undefined, options?: { isFile: boolean }, abortController?: RefObject<AbortController | undefined>) => {
  const user = await userManager.getUser()
  let res
  try {
    const path = getPath(url)
    res = await fetch(path, { headers: { ...headers, Authorization: `Bearer ${user?.access_token}` }, ...args, signal: abortController?.current?.signal })
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : 'unknown error in request'
    return {
      status: 0,
      statusText: e instanceof Error ? e.message : 'Failed to fetch',
      ok: false,
      body: {},
      aborted: abortController?.current?.signal.aborted || errorMessage === 'The user aborted a request.',
    }
  }

  let resBody
  try {
    if (options?.isFile) return res
    resBody = await res.json()
  } catch (e) {
    // sometimes GET requests dont return any payload - i.e. GET /api/forgot-password
    resBody = {
      detail: e,
    }
  }

  const returnObject = {
    status: res?.status,
    statusText: res?.statusText,
    ok: res?.ok,
    body: resBody,
  }

  return returnObject
}

const post = async (url: string, body: object, args?: RequestInit | undefined, abortController?: RefObject<AbortController | undefined>) => {
  const user = await userManager.getUser()
  let controller
  if (abortController) {
    // if a previous abort controller is defined, abort the request
    if (abortController.current) {
      abortController.current.abort()
    }
    // assign the new abort controller to the passed in ref
    // this value should be passed into every request from that component
    // if it wants aborted requests
    // eslint-disable-next-line no-param-reassign
    abortController.current = new AbortController()
    controller = abortController.current
  }

  const path = getPath(url)

  let res
  try {
    res = await fetch(path, {
      headers: {
        ...headers,
        Authorization: `Bearer ${user?.access_token}`,
      },
      method: 'POST',
      body: JSON.stringify(body),
      ...args,
      signal: controller?.signal,
    })
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : 'unknown error in request'
    return {
      ok: false,
      body: {},
      status: 500,
      statusText: errorMessage,
      aborted: controller?.signal.aborted || errorMessage === 'The user aborted a request.',
    }
  }

  let resBody
  try {
    resBody = await res.json()
  } catch (e) {
    if (!res.ok) {
      resBody = {
        detail: e,
      }
    }
  }

  const returnObject = {
    status: res.status,
    statusText: res.statusText,
    ok: res.ok,
    body: resBody,
    aborted: controller?.signal.aborted,
  }

  return returnObject
}

const put = async (url: string, body: object | FormData, args?: RequestInit | undefined) => {
  const user = await userManager.getUser()
  const path = getPath(url)
  const isFormData = body instanceof FormData

  const putCallHeaders: Record<string, string | undefined> = {
    ...headers,
  }

  // need to delete explicitly so that browser can set correct boundary for form data calls (i.e. image upload)
  if (isFormData) {
    delete putCallHeaders['Content-Type']
  }

  const res = await fetch(path, {
    headers: {
      ...putCallHeaders,
      Authorization: `Bearer ${user?.access_token}`,
    },
    method: 'PUT',
    body: isFormData ? body : JSON.stringify(body),
    ...args,
  })

  let resBody
  try {
    resBody = await res.json()
  } catch (e) {
    if (!res.ok) {
      resBody = {
        detail: e,
      }
    }
  }

  const returnObject = {
    status: res.status,
    statusText: res.statusText,
    ok: res.ok,
    body: resBody,
  }

  return returnObject
}

const patch = async (url: string, body: object, args?: RequestInit | undefined) => {
  const user = await userManager.getUser()
  const path = getPath(url)

  const res = await fetch(path, {
    headers: {
      ...headers,
      Authorization: `Bearer ${user?.access_token}`,
    },
    method: 'PATCH',
    body: JSON.stringify(body),
    ...args,
  })

  let resBody
  try {
    resBody = await res.json()
  } catch (e) {
    if (!res.ok) {
      resBody = {
        detail: e,
      }
    }
  }

  const returnObject = {
    status: res.status,
    statusText: res.statusText,
    ok: res.ok,
    body: resBody,
  }

  return returnObject
}

const del = async (url: string, args?: RequestInit | undefined) => {
  const user = await userManager.getUser()
  const path = getPath(url)

  const res = await fetch(path, {
    headers: {
      ...headers,
      Authorization: `Bearer ${user?.access_token}`,
    },
    method: 'DELETE',
    ...args,
  })

  let resBody
  try {
    resBody = await res.json()
  } catch (e) {
    if (!res.ok) {
      resBody = {
        detail: e,
      }
    }
  }

  const returnObject = {
    status: res.status,
    statusText: res.statusText,
    ok: res.ok,
    body: resBody,
  }

  return returnObject
}

const Api = {
  get,
  post,
  put,
  patch,
  del,
}

export default Api
